import { useEffect, useRef } from "react";
import { useT } from "../i18n";
import { stripLang } from "../i18n/paths";

// Google AdSense. The publisher ID is public (it's also in index.html and
// ads.txt), and so are the ad unit slot IDs (AdSense → Ads → By ad unit).
// Production builds use the live site's units; development shows placeholders
// unless .env sets them (see README):
//   REACT_APP_ADSENSE_SLOT_BANNER / _SIDEBAR / _INTERMISSION=<slot ids>
export const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "ca-pub-5113970918251949";
const CLIENT = ADSENSE_CLIENT;
const LIVE = process.env.NODE_ENV === "production";
const SLOTS = {
  banner: process.env.REACT_APP_ADSENSE_SLOT_BANNER || (LIVE ? "1052719956" : null),
  sidebar: process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR || (LIVE ? "5516288153" : null),
  intermission: process.env.REACT_APP_ADSENSE_SLOT_INTERMISSION || (LIVE ? "4800393279" : null),
};

let scriptLoaded = false;
function loadScript() {
  if (scriptLoaded || !CLIENT) return;
  // index.html already includes the AdSense script; only add it if it's missing.
  if (document.querySelector('script[src*="adsbygoogle.js"]')) {
    scriptLoaded = true;
    return;
  }
  scriptLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT}`;
  s.crossOrigin = "anonymous";
  document.head.appendChild(s);
}

// Google only allows ads on pages with real publisher content, so ads show on
// the home page, game pages, guides and About - not in lobbies or rooms (/r/…),
// which are mostly app screens.
const hasContent = (pathname) => {
  const p = stripLang(pathname);
  return p === "/" || p.startsWith("/games/") || p === "/about" || p === "/guides" || p.startsWith("/guides/");
};

export default function AdSlot({ kind = "banner", className = "" }) {
  const t = useT();
  // Read once per mount: moving between pages mounts a fresh AdSlot.
  const allowed = hasContent(window.location.pathname);
  const insRef = useRef(null);
  const slot = SLOTS[kind];

  useEffect(() => {
    if (!allowed || !CLIENT || !slot) return;
    loadScript();
    try {
      if (insRef.current && !insRef.current.dataset.adsbygoogleStatus) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      // Ad blockers throw here; the game works fine without ads.
    }
  }, [slot, allowed]);

  if (!allowed) return null;
  if (!CLIENT || !slot) {
    if (process.env.NODE_ENV === "production") return null;
    return (
      <div className={`ad-slot ad-placeholder ad-${kind} ${className}`}>
        <span>📢 Ad space ({kind})</span>
        <small>Set REACT_APP_ADSENSE_* in .env</small>
      </div>
    );
  }

  return (
    <div className={`ad-slot ad-${kind} ${className}`}>
      <span className="ad-label">{t("Advertisement")}</span>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={CLIENT}
        data-ad-slot={slot}
        data-ad-format={kind === "sidebar" ? "vertical" : "auto"}
        data-full-width-responsive="true"
      />
    </div>
  );
}
